import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useSiteSettings } from './SiteSettingsContext';
import { publicSettingsAPI } from '../services/api';

const SystemModeContext = createContext();

export const useSystemMode = () => {
  const context = useContext(SystemModeContext);
  if (!context) {
    throw new Error('useSystemMode must be used within a SystemModeProvider');
  }
  return context;
};

export const SystemModeProvider = ({ children }) => {
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [comingSoonMode, setComingSoonMode] = useState(false);
  const [pageSettings, setPageSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin } = useAuth();
  const { loading: settingsLoading } = useSiteSettings();

  // Fetch system mode settings
  const fetchSystemModeSettings = async () => {
    try {
      setLoading(true);
      const response = await publicSettingsAPI.getSettings();
      
      if (response?.data?.success && response.data.data?.settings) {
        const settings = response.data.data.settings;
        
        // Extract global system mode settings
        const maintenanceModeSetting = settings.maintenance_mode?.value;
        const comingSoonModeSetting = settings.coming_soon_mode?.value;
        
        setMaintenanceMode(maintenanceModeSetting === 'true' || maintenanceModeSetting === true);
        setComingSoonMode(comingSoonModeSetting === 'true' || comingSoonModeSetting === true);
        
        // Extract page-level settings
        const pageSettingsObj = {};
        Object.keys(settings).forEach(key => {
          if (key.includes('_maintenance_mode') || key.includes('_coming_soon_mode')) {
            pageSettingsObj[key] = settings[key]?.value === 'true' || settings[key]?.value === true;
          }
        });
        setPageSettings(pageSettingsObj);
      }
    } catch (error) {
      console.error('Error fetching system mode settings:', error);
      // Keep default values on error
    } finally {
      setLoading(false);
    }
  };

  // Get current page key from pathname
  const getCurrentPageKey = (pathname) => {
    const path = pathname === '/' ? 'home' : pathname.slice(1).replace(/\//g, '_');
    return path;
  };

  // Check if current route should be accessible
  const shouldRedirect = () => {
    // Don't redirect if still loading
    if (loading || settingsLoading) return false;
    
    // Don't redirect if user is admin
    if (isAdmin()) return false;
    
    // Don't redirect if already on maintenance or coming soon pages
    if (location.pathname === '/maintenance' || location.pathname === '/coming-soon') return false;
    
    // Don't redirect for admin routes
    if (location.pathname.startsWith('/admin')) return false;
    
    // Don't redirect for auth routes
    if (location.pathname.startsWith('/login') || 
        location.pathname.startsWith('/register') || 
        location.pathname.startsWith('/forgot-password') || 
        location.pathname.startsWith('/reset-password')) return false;
    
    // Check global modes first (they override page-level settings)
    if (maintenanceMode || comingSoonMode) return true;
    
    // Check page-level settings
    const currentPageKey = getCurrentPageKey(location.pathname);
    const pageMaintenanceKey = `${currentPageKey}_maintenance_mode`;
    const pageComingSoonKey = `${currentPageKey}_coming_soon_mode`;
    
    return pageSettings[pageMaintenanceKey] || pageSettings[pageComingSoonKey];
  };

  // Get the appropriate redirect path
  const getRedirectPath = () => {
    // Check global modes first
    if (maintenanceMode) return '/maintenance';
    if (comingSoonMode) return '/coming-soon';
    
    // Check page-level settings
    const currentPageKey = getCurrentPageKey(location.pathname);
    const pageMaintenanceKey = `${currentPageKey}_maintenance_mode`;
    const pageComingSoonKey = `${currentPageKey}_coming_soon_mode`;
    
    // For individual page settings, redirect to the same page but with a mode parameter
    if (pageSettings[pageMaintenanceKey]) return `${location.pathname}?mode=maintenance`;
    if (pageSettings[pageComingSoonKey]) return `${location.pathname}?mode=coming-soon`;
    
    return null;
  };

  // Handle redirects
  useEffect(() => {
    if (shouldRedirect()) {
      const redirectPath = getRedirectPath();
      if (redirectPath && location.pathname !== redirectPath) {
        navigate(redirectPath);
      }
    }
  }, [maintenanceMode, comingSoonMode, pageSettings, location.pathname, loading, settingsLoading, isAdmin]);

  // Fetch settings on mount
  useEffect(() => {
    fetchSystemModeSettings();
  }, []);

  const value = {
    maintenanceMode,
    comingSoonMode,
    pageSettings,
    loading,
    fetchSystemModeSettings
  };

  return (
    <SystemModeContext.Provider value={value}>
      {children}
    </SystemModeContext.Provider>
  );
};
